import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { ContactBalance } from '@modules-deprecated/app/contacts'
import { useFetchContact } from '@views/contacts/hooks/useFetchContact'
import { useFetchContactBalance } from '@views/contacts/hooks/useFetchContactBalance'

import { Side } from '../../../enums/side'
import { BillType } from '../../bills/enums/billType'
import { Contact } from '../../contacts/types/contact'
import { PayableSubjectType } from '../types/payableSubjectType'

interface ContextState {
  canUsePrepayment: boolean
  contact: Contact | undefined
  contactBalance: ContactBalance | undefined
}

const ContactBalanceContext = createContext<ContextState | undefined>(undefined)

interface ContactBalanceContextProps {
  subjectType?: PayableSubjectType | null
  contactId?: string
  currencyId?: string
  children?: ReactNode
}

export const ContactBalanceContextProvider = ({
  subjectType,
  contactId,
  currencyId,
  children,
}: ContactBalanceContextProps): ReactElement => {
  const { contact } = useFetchContact(contactId)
  const { contactBalance } = useFetchContactBalance(contact?.id, currencyId)

  const billsCanUsePrepayment = contactBalance?.side === Side.Debit
  const invoicesCanUsePrepayment = contactBalance?.side === Side.Credit

  const canUsePrepayment = useMemo(() => {
    if (!contactBalance?.amount) {
      return false
    }
    switch (subjectType) {
      case BillType.Bill:
        return billsCanUsePrepayment
      case InvoiceType.Invoice:
        return invoicesCanUsePrepayment
      default:
        return false
    }
  }, [billsCanUsePrepayment, contactBalance?.amount, invoicesCanUsePrepayment, subjectType])

  return (
    <ContactBalanceContext.Provider
      value={{
        contact,
        canUsePrepayment,
        contactBalance,
      }}
    >
      {children}
    </ContactBalanceContext.Provider>
  )
}

export const useContactBalance = () => {
  const context = useContext(ContactBalanceContext)
  if (!context) {
    throw new Error('ContactBalanceContextProvider is missing in the module!')
  }

  return context
}
